import type { Configuration } from '@repo/api-gw-sdk';

import type {
  AvailableRepository,
  ConnectGitRepositoryInput,
  CreatePullRequestInput,
  GitIntegration,
  GitCommitResult,
  GitDiffResponse,
  GitDraft,
  GitFileResponse,
  GitMergeResult,
  GitPullRequest,
  GitRepository,
  GitTreeResponse,
  RepositoryAsset,
} from '@/features/lakehouse/routes/repositories/repositoriesTypes';

import type { HttpClient } from './httpClient';

interface SwrResult<T> {
  body: T | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: () => void;
}

const newIdempotencyKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;

// Workspace-scoped Git repository access, mirroring the Lakehouse OpenAPI
// `git-repositories` surface. Written against raw httpClient because the
// generated SDK does not yet expose these operations; migrate onto the SDK
// once it does. The actor identity (`X-Eon-User-Id`) is injected by the API
// gateway from the authenticated principal, never sent from the browser (a
// client-supplied value is spoofable and trips CORS preflight). Draft mutations
// and PR create/merge carry `If-Match` (draft head SHA) and a fresh
// `Idempotency-Key`.
export const gitRepositories = (
  httpClient: HttpClient,
  _config: Configuration,
  currentProjectId: string
) => {
  const root = (workspaceId: string) =>
    `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-repositories`;

  // Requests with a JSON body must declare Content-Type or the API gateway's
  // request validation rejects them (400). The actor identity is injected by the
  // gateway, so no identity header is sent from the browser.
  const jsonHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const getJson = (url: string) => httpClient.request('GET', url);

  const postJson = (url: string, body: unknown) =>
    httpClient.request('POST', url, {
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });

  const useGet = <T>(url: string | null): SwrResult<T> => {
    const res = httpClient.executeWithConfig(getJson, url, '', {}, url ?? '');
    return {
      body: res.data,
      isLoading: res.isLoading,
      error: res.error,
      mutate: () => void res.mutate(),
    };
  };

  const draftHeaders = (ifMatch: string) => ({
    ...jsonHeaders,
    'If-Match': ifMatch,
    'Idempotency-Key': newIdempotencyKey(),
  });

  return {
    useList: (
      workspaceId: string
    ): SwrResult<{ gitRepositories: GitRepository[]; nextToken?: string }> => {
      const url = workspaceId ? `${root(workspaceId)}/list?pageSize=200` : null;
      const res = httpClient.executeWithConfig(
        postJson,
        url,
        '',
        {},
        url ?? '',
        {}
      );
      return {
        body: res.data,
        isLoading: res.isLoading,
        error: res.error,
        mutate: () => void res.mutate(),
      };
    },

    useRepository: (
      workspaceId: string,
      repositoryId: string
    ): SwrResult<GitRepository> =>
      useGet(
        workspaceId && repositoryId
          ? `${root(workspaceId)}/${repositoryId}`
          : null
      ),

    useAvailableRepositories: (
      workspaceId: string
    ): SwrResult<{ repositories: AvailableRepository[] }> =>
      useGet(
        workspaceId
          ? `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-integrations/available-repositories`
          : null
      ),

    useIntegrations: (
      workspaceId: string
    ): SwrResult<{ integrations: GitIntegration[] }> =>
      useGet(
        workspaceId
          ? `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-integrations`
          : null
      ),

    // Returns the GitHub install/authorize URL to redirect the browser to.
    // `returnUrl` is this console's origin; the fixed callback host redirects
    // back here after binding (validated server-side against a host allowlist).
    startGitHubAuthorization: (
      workspaceId: string,
      returnUrl: string
    ): Promise<{ authorizeUrl: string }> =>
      postJson(
        `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-integrations/github/authorize`,
        { returnUrl }
      ),

    // Binds the installation to the project after the GitHub callback.
    completeGitIntegration: (
      workspaceId: string,
      input: { code: string; installationId: string; state?: string }
    ): Promise<GitIntegration> =>
      postJson(
        `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-integrations/github/complete`,
        input
      ),

    deleteIntegration: (
      workspaceId: string,
      integrationId: string
    ): Promise<void> =>
      httpClient.request(
        'DELETE',
        `/projects/${currentProjectId}/lakehouse/workspace/${workspaceId}/git-integrations/${integrationId}`
      ),

    useDraft: (
      workspaceId: string,
      repositoryId: string,
      enabled: boolean
    ): SwrResult<GitDraft> =>
      useGet(
        enabled && workspaceId && repositoryId
          ? `${root(workspaceId)}/${repositoryId}/draft`
          : null
      ),

    useTree: (
      workspaceId: string,
      repositoryId: string,
      ref: string,
      path: string,
      enabled: boolean
    ): SwrResult<GitTreeResponse> => {
      const query = new URLSearchParams({ ref, pageSize: '200' });
      if (path) {
        query.set('path', path);
      }
      return useGet(
        enabled && workspaceId && repositoryId
          ? `${root(workspaceId)}/${repositoryId}/tree?${query.toString()}`
          : null
      );
    },

    useFile: (
      workspaceId: string,
      repositoryId: string,
      ref: string,
      path: string | undefined,
      enabled = true
    ): SwrResult<GitFileResponse> => {
      const query = new URLSearchParams({ ref, path: path ?? '' });
      return useGet(
        enabled && workspaceId && repositoryId && path
          ? `${root(workspaceId)}/${repositoryId}/files?${query.toString()}`
          : null
      );
    },

    useDiff: (
      workspaceId: string,
      repositoryId: string,
      enabled: boolean
    ): SwrResult<GitDiffResponse> =>
      useGet(
        enabled && workspaceId && repositoryId
          ? `${root(workspaceId)}/${repositoryId}/draft/diff`
          : null
      ),

    useAssets: (
      workspaceId: string,
      repositoryId: string,
      ref: string,
      enabled: boolean
    ): SwrResult<{ assets: RepositoryAsset[] }> => {
      const query = new URLSearchParams({ ref });
      return useGet(
        enabled && workspaceId && repositoryId
          ? `${root(workspaceId)}/${repositoryId}/assets?${query.toString()}`
          : null
      );
    },

    connect: (
      workspaceId: string,
      input: ConnectGitRepositoryInput
    ): Promise<GitRepository> =>
      httpClient.request('POST', root(workspaceId), {
        headers: jsonHeaders,
        body: JSON.stringify(input),
      }),

    disconnect: (workspaceId: string, repositoryId: string): Promise<void> =>
      httpClient.request('DELETE', `${root(workspaceId)}/${repositoryId}`),

    refresh: (
      workspaceId: string,
      repositoryId: string
    ): Promise<GitRepository> =>
      httpClient.request(
        'POST',
        `${root(workspaceId)}/${repositoryId}/refresh`
      ),

    putDraftFile: (
      workspaceId: string,
      repositoryId: string,
      path: string,
      content: string,
      ifMatch: string
    ): Promise<GitCommitResult> =>
      httpClient.request(
        'PUT',
        `${root(workspaceId)}/${repositoryId}/draft/files?path=${encodeURIComponent(path)}`,
        { headers: draftHeaders(ifMatch), body: JSON.stringify({ content }) }
      ),

    deleteDraftFile: (
      workspaceId: string,
      repositoryId: string,
      path: string,
      ifMatch: string
    ): Promise<GitCommitResult> =>
      httpClient.request(
        'DELETE',
        `${root(workspaceId)}/${repositoryId}/draft/files?path=${encodeURIComponent(path)}`,
        { headers: draftHeaders(ifMatch) }
      ),

    moveDraftFile: (
      workspaceId: string,
      repositoryId: string,
      fromPath: string,
      toPath: string,
      ifMatch: string
    ): Promise<GitCommitResult> =>
      httpClient.request(
        'POST',
        `${root(workspaceId)}/${repositoryId}/draft/moves`,
        {
          headers: draftHeaders(ifMatch),
          body: JSON.stringify({ fromPath, toPath }),
        }
      ),

    createPullRequest: (
      workspaceId: string,
      repositoryId: string,
      ifMatch: string,
      input: CreatePullRequestInput
    ): Promise<GitPullRequest> =>
      httpClient.request(
        'POST',
        `${root(workspaceId)}/${repositoryId}/draft/pull-request`,
        { headers: draftHeaders(ifMatch), body: JSON.stringify(input) }
      ),

    mergePullRequest: (
      workspaceId: string,
      repositoryId: string,
      ifMatch: string
    ): Promise<GitMergeResult> =>
      httpClient.request(
        'PUT',
        `${root(workspaceId)}/${repositoryId}/draft/pull-request/merge`,
        { headers: draftHeaders(ifMatch) }
      ),
  };
};
